import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Spinner from "../spinner/Spinner";
import useGetMultipleProfilePicture from "../../hooks/useGetMultipleProfilePicture";
import {
  PostCardMessageSectionProps,
  HashTagPropsCardList,
} from "../../types/ComponentProps";
import PostCardStyles from "./PostCardStyles.module.css";

const PostCardMessage = ({
  message,
  recipients,
  hashTagIds,
  badgeId,
  badgeData,
  badgeDataLoading,
}: PostCardMessageSectionProps) => {
  const rec = (recipients || []).map((data) => data?.id).filter(Boolean);

  const { isLoading, multipleProfilePictureData } =
    useGetMultipleProfilePicture(rec);

  const getRandomImages = (images: any[], count: number) => {
    const shuffled = images.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  const randomImages = useMemo(() => {
    return getRandomImages(multipleProfilePictureData?.profileImages || [], 3);
  }, [multipleProfilePictureData?.profileImages]);

  const isUrl = (str: string) => {
    const urlRegex =
      /^(https?:\/\/[^\s]+|www\.[^\s]+\.[a-z]{2,}|[^\s]+\.[a-z]{2,})(?=\s|$)/i;
    return urlRegex.test(str);
  };

  const normalizeUrl = (str: string) => {
    if (str.startsWith("www.")) {
      return `https://${str}`;
    }
    if (!str.startsWith("http") && str.match(/\.[a-z]{2,}$/i)) {
      return `https://${str}`;
    }
    return str;
  };

  const renderMessageContent = (message: string) => {
    if (!message) return null;

    const parts = message.split(/(https?:\/\/[^\s]+|www\.[^\s]+\.[a-z]{2,})/i);

    return parts.map((part, index) =>
      isUrl(part) ? (
        <a
          key={index}
          href={normalizeUrl(part)}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#fff" }}
          className={PostCardStyles.messageLink}
        >
          {part}
        </a>
      ) : (
        <span key={index}>{part}</span>
      ),
    );
  };

  const renderMessageWithMentions = (
    msg: string,
    recipientsList: typeof recipients,
  ) => {
    if (!msg) return null;

    // Build a combined regex for all chip types: @mention, #hashtag, +points
    const patterns: string[] = [];

    // @mentions
    if (recipientsList && recipientsList.length > 0) {
      const nicknames = recipientsList.map((user) =>
        user.preferredName.replace(/\s+/g, ""),
      );
      const escapedNicknames = nicknames.map((n) =>
        n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      );
      patterns.push(`@(?:${escapedNicknames.join("|")})(?=\\s|$|\\b)`);
    }

    // #hashtags
    if (hashTagIds && hashTagIds.length > 0) {
      const tagNames = hashTagIds.map((t: any) =>
        (t.hashTagName || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      );
      patterns.push(`#(?:${tagNames.join("|")})(?=\\s|$|\\b)`);
    }

    // +points (any +number)
    patterns.push(`\\+\\d+(?=\\s|$)`);

    // $coins (any $number)
    patterns.push(`\\$\\d+(?=\\s|$)`);

    if (patterns.length === 0) {
      return renderMessageContent(msg);
    }

    const combinedPattern = new RegExp(`(${patterns.join("|")})`, "gi");
    const parts = msg.split(combinedPattern);

    // Track which recipients were matched inline
    const matchedIds = new Set<number>();

    const inlineContent = parts.map((part, index) => {
      if (!part) return null;

      // Check @mention
      if (part.startsWith("@") && recipientsList) {
        const matchedUser = recipientsList.find(
          (user) =>
            `@${user.preferredName.replace(/\s+/g, "")}`.toLowerCase() ===
            part.toLowerCase(),
        );
        if (matchedUser) {
          matchedIds.add(matchedUser.id);
          return (
            <Link
              to={`/profile/${matchedUser.id}`}
              className={PostCardStyles.linkTaggedUsers}
              key={`mention-${index}`}
            >
              <span className={PostCardStyles.recipientListBlk}>
                {matchedUser.preferredName}
              </span>
            </Link>
          );
        }
      }

      // Check #hashtag
      if (part.startsWith("#") && hashTagIds) {
        const matchedTag = hashTagIds.find(
          (t: any) => `#${t.hashTagName}`.toLowerCase() === part.toLowerCase(),
        );
        if (matchedTag) {
          return (
            <span
              className={PostCardStyles.spanHashTagSection}
              key={`hashtag-${index}`}
            >
              {(matchedTag as HashTagPropsCardList).hashTagName}
            </span>
          );
        }
      }

      // Check +points
      if (/^\+\d+$/.test(part)) {
        return (
          <span
            className={PostCardStyles.pointsChipDisplay}
            key={`points-${index}`}
          >
            {part.slice(1)} pts
          </span>
        );
      }

      // Check $coins
      if (/^\$\d+$/.test(part)) {
        return (
          <span
            className={PostCardStyles.coinsChipDisplay}
            key={`coins-${index}`}
          >
            {part.slice(1)} coins
          </span>
        );
      }

      // Render normal text (with URL detection)
      return (
        <React.Fragment key={index}>
          {renderMessageContent(part)}
        </React.Fragment>
      );
    });

    // Fallback: append any recipients not found inline (old posts)
    const unmatchedRecipients = (recipientsList || []).filter(
      (user) => !matchedIds.has(user.id),
    );

    const fallbackChips = unmatchedRecipients.map((data, index) => (
      <Link
        to={`/profile/${data.id}`}
        className={PostCardStyles.linkTaggedUsers}
        key={`fallback-${index}`}
      >
        <span className={PostCardStyles.recipientListBlk}>
          {data.preferredName}
        </span>
      </Link>
    ));

    // Fallback: append any hashtags not found inline (old posts)
    const matchedHashtagNames = new Set(
      parts
        .filter((p) => p && p.startsWith("#"))
        .map((p) => p.slice(1).toLowerCase()),
    );
    const unmatchedHashtags = (hashTagIds || []).filter(
      (t: any) => !matchedHashtagNames.has((t.hashTagName || "").toLowerCase()),
    );
    const fallbackHashtags = unmatchedHashtags.map(
      (data: any, index: number) => (
        <span
          className={PostCardStyles.spanHashTagSection}
          key={`fallback-ht-${index}`}
        >
          {data.hashTagName}
        </span>
      ),
    );

    return (
      <>
        {inlineContent}
        {fallbackChips}
        {fallbackHashtags}
      </>
    );
  };

  return (
    <div>
      {badgeId ? (
        <div className={PostCardStyles.badgeSection}>
          {badgeDataLoading ? (
            <Spinner variant="small-spinner" alignSpin="center-spinner" />
          ) : (
            <>
              <img src={badgeData?.imageURL} alt="Badge" />
              <h1>
                {badgeData?.badgeName?.startsWith("Congratulations!") ? (
                  <>
                    Congratulations!
                    <br />
                    <span
                      style={{
                        fontSize: "20px",
                        fontWeight: 400,
                        marginTop: 8,
                      }}
                    >
                      {badgeData.badgeName.replace(/^Congratulations!\s*/, "")}
                    </span>
                  </>
                ) : (
                  badgeData?.badgeName
                )}
              </h1>
            </>
          )}

          <div className={PostCardStyles.multipleImageSelection}>
            {isLoading ? (
              <Spinner variant="medium-spinner" alignSpin="center-spinner" />
            ) : (
              randomImages.map((data: any, index: number) => (
                <img src={data?.profileImage} alt="" key={index} />
              ))
            )}
          </div>
        </div>
      ) : null}

      {badgeId && <hr className={PostCardStyles.hrStyles} />}
      <p className={PostCardStyles.messageBlockSection}>
        {renderMessageWithMentions(message || "", recipients || [])}
      </p>
    </div>
  );
};

export default PostCardMessage;
